// ParagraphBlockEditor — contentEditable for inline-formatted body text.
//
// Implementation notes:
//   • contentEditable div, controlled-but-not-rerendered. We only set
//     innerHTML when block.html actually differs from the live DOM,
//     because re-setting it on every parent render would wipe the user's
//     caret position.
//   • On input we run sanitizeInlineHtml() and bubble up the cleaned
//     HTML. Sanitizer drops disallowed tags (e.g. <div>, <span>, <font>)
//     and inline styles — keeps stored HTML on the narrow whitelist.
//   • Enter creates a new empty paragraph block after this one
//     (notifies parent via onInsertAfter).
//   • "/" at the start of an empty block opens the slash menu.
//   • InlineFormatToolbar is shown when the user has a non-empty
//     selection inside this element.

import { useEffect, useRef, useState } from "react";
import type { ParagraphBlock, Block, Align } from "../../../lib/blocks";
import { sanitizeInlineHtml } from "../../../lib/blocks";
import { markdownToBlocks } from "../../../lib/markdownToBlocks";
import { htmlToBlocks } from "../../../lib/htmlToBlocks";
import { InlineFormatToolbar } from "../InlineFormatToolbar";
import { t } from "../theme";

export interface ParagraphBlockEditorProps {
  block: ParagraphBlock;
  onChange: (patch: Partial<Block>) => void;
  isSelected: boolean;
  onSlashMenu: () => void;
  onInsertAfter: () => void;
  /** Если paste распарсился в массив блоков — отдаём наверх. */
  onPasteBlocks?: (blocks: Block[]) => void;
}

// Эвристика: похоже ли это на Markdown / большой rich-text, который стоит
// преобразовать в блоки? Короткие однострочные вставки оставляем браузеру.
function looksLikeMarkdown(text: string): boolean {
  if (!text) return false;
  if (/^#{1,3}\s/m.test(text)) return true;
  if (/^>\s/m.test(text)) return true;
  if (/^[-*+]\s/m.test(text)) return true;
  if (/^\d+\.\s/m.test(text)) return true;
  if (/!\[[^\]]*\]\([^)]+\)/.test(text)) return true;
  if (/^(?:-{3,}|\*{3,}|_{3,})\s*$/m.test(text)) return true;
  // Несколько параграфов (blank line между ними) — тоже трактуем как
  // структурный вставленный текст.
  if (/\n\s*\n/.test(text)) return true;
  return false;
}

const SIZE_PRESET = {
  small:   { font: 13, line: 1.6 },
  regular: { font: 16, line: 1.7 },
  large:   { font: 18, line: 1.7 },
  lead:    { font: 22, line: 1.55 },
} as const;

export function ParagraphBlockEditor({
  block,
  onChange,
  isSelected,
  onSlashMenu,
  onInsertAfter,
  onPasteBlocks,
}: ParagraphBlockEditorProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [hostEl, setHostEl] = useState<HTMLElement | null>(null);

  // Sync prop → DOM only when external value changes.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.innerHTML !== (block.html || "")) {
      el.innerHTML = block.html || "";
    }
  }, [block.html]);

  useEffect(() => {
    setHostEl(ref.current);
  }, []);

  function emit() {
    const el = ref.current;
    if (!el) return;
    const clean = sanitizeInlineHtml(el.innerHTML);
    if (clean !== block.html) onChange({ html: clean } as Partial<ParagraphBlock>);
  }

  const preset = SIZE_PRESET[block.size || "regular"];

  return (
    <div style={{ position: "relative" }}>
      {isSelected && (
        <div
          style={{
            display: "flex",
            gap: 6,
            marginBottom: 6,
            fontFamily: t.sans,
          }}
        >
          {(["small", "regular", "large", "lead"] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onChange({ size: s } as Partial<ParagraphBlock>)}
              style={pillStyle((block.size || "regular") === s)}
            >
              {s}
            </button>
          ))}
          <div style={{ width: 8 }} />
          {(["left", "center", "right"] as Align[]).map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => onChange({ align: a } as Partial<ParagraphBlock>)}
              style={pillStyle((block.align || "left") === a)}
            >
              {a}
            </button>
          ))}
        </div>
      )}
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        data-placeholder='Type "/" for blocks…'
        onInput={emit}
        onBlur={emit}
        onPaste={(e) => {
          // Magic Paste: detect Markdown / rich HTML and split into blocks.
          const cd = e.clipboardData;
          if (!cd) return;
          const html = cd.getData("text/html") || "";
          const text = cd.getData("text/plain") || "";

          // Тривиальная вставка — не трогаем, отдаём браузеру.
          const isShort =
            text.length < 200 &&
            !text.includes("\n") &&
            !looksLikeMarkdown(text);
          if (isShort && !html) return;

          let parsed: Block[] = [];

          // Сначала HTML — если есть и содержит структурные теги
          // (Notion/Google Docs).
          if (
            html &&
            html.length > 200 &&
            /<(h[1-3]|p|ul|ol|blockquote|figure|img|hr)\b/i.test(html)
          ) {
            try {
              parsed = htmlToBlocks(html);
            } catch {
              parsed = [];
            }
          }

          // Если HTML не дал блоков — пробуем Markdown.
          if (parsed.length === 0 && text && looksLikeMarkdown(text)) {
            try {
              parsed = markdownToBlocks(text);
            } catch {
              parsed = [];
            }
          }

          // Никаких блоков — пускай браузер вставит обычно.
          if (parsed.length === 0 || !onPasteBlocks) return;

          // Перехватываем дефолтный paste, пушим блоки наверх.
          e.preventDefault();
          emit();
          onPasteBlocks(parsed);
        }}
        onKeyDown={(e) => {
          // Enter -> new paragraph block.
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            emit();
            onInsertAfter();
            return;
          }
          // "/" at the start of an empty paragraph -> slash menu.
          if (e.key === "/" && !block.html.trim()) {
            e.preventDefault();
            onSlashMenu();
            return;
          }
        }}
        style={{
          minHeight: "1.6em",
          outline: "none",
          fontFamily: t.serif,
          fontSize: preset.font,
          lineHeight: preset.line,
          color: t.text,
          textAlign: block.align || "left",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      />
      {isSelected && (
        <InlineFormatToolbar host={hostEl} onAfterCommand={emit} />
      )}
      <style>
        {`
          [contenteditable][data-placeholder]:empty:before {
            content: attr(data-placeholder);
            color: ${t.text4};
            pointer-events: none;
          }
        `}
      </style>
    </div>
  );
}

function pillStyle(active: boolean): React.CSSProperties {
  return {
    padding: "3px 8px",
    fontSize: 11,
    borderRadius: 4,
    background: active ? t.accentSoft : "transparent",
    color: active ? t.accent : t.text3,
    border: `1px solid ${active ? t.accentStrong : t.border}`,
    fontFamily: t.sans,
    cursor: "pointer",
    textTransform: "capitalize",
  };
}
